/**
 * Marco de enfoque de la cámara y su barra de progreso (004 · US1 · T029; FR-002; QA-4 de la 005;
 * design-system §8.1 · "Flujo de marcación (cámara)").
 *
 * - Marco centrado **estático** con radio `radius-xl`, borde de 2 px blanco al 80 % y **esquinas
 *   más gruesas**; fuera del marco, un velo `rgba(20,28,43,0.55)` que se dibuja con un
 *   `box-shadow` enorme, así no hace falta recortar nada ni superponer cuatro paneles.
 * - **QA-4 de la 005: ya no hay anillo que gire.** El arco coral indeterminado de "Buscando
 *   marcador…" se retiró por pedido del usuario (distraía y no decía nada que el texto de estado no
 *   dijera ya). Mientras se busca, el marco no tiene ningún indicador: el estado lo dice el texto
 *   (`aria-live`) de `camera-stage.tsx`.
 * - **Barra fina de progreso determinado, solo con `progress`** ("Verificando…"): 4 px, coral sobre
 *   una pista blanca tenue, **fuera del marco, debajo**, del mismo ancho. Vive en posición absoluta
 *   dentro del hueco superior de `marking-frame-lines` (`pt-fm-4`), así que aparecer y desaparecer
 *   no mueve ni el marco (que está anclado al centro del recorte, T096) ni los textos. Avanza con
 *   `duration-fm-base` (180 ms, dentro de 120–240 ms) y, con `prefers-reduced-motion`, salta sin
 *   animar (`motion-reduce:transition-none`, y el token ya vale 0 ms).
 * - El estado nunca se comunica solo por la barra (§10): lleva `role="progressbar"` con `label`
 *   como nombre accesible y la pantalla pone además el texto en una región `aria-live`.
 *
 * > 🔴 **T096 · el tamaño del marco ya no lo decide este archivo.** Lo decide el recorte real de la
 * > captura: `sidePx` llega calculado por `roiOverlaySide` desde el ROI que el bucle está usando
 * > en ese momento (`src/camera/roi-controller.ts`). Poner aquí un tamaño propio —como el
 * > `min(72vw, 320px)` que había— hace que el trabajador encuadre según una guía que no
 * > corresponde a lo que se lee. `FRAME_SIDE` sobrevive **solo** como reserva para cuando todavía
 * > no hay fotograma y, por tanto, tampoco hay recorte.
 * >
 * > El marco cambia de tamaño mientras se busca (el recorte barre una escalera) y se queda quieto
 * > en cuanto engancha. La transición es `duration-fm-base`, y con `prefers-reduced-motion`
 * > desaparece: el marco salta de un tamaño al siguiente sin animar. El barrido en sí **no** se
 * > puede desactivar porque no es decoración: es lo que encuentra el código.
 *
 * El rango operativo sigue siendo 25–35 cm (research R-5): **acercarse más rompe el enfoque** en
 * los iPhone Pro recientes.
 */
import { cn } from '../../components/ui/cn';

import type { ReactNode } from 'react';

/** Reserva mientras la cámara no entrega fotograma (y por tanto no hay recorte que dibujar). */
const FRAME_SIDE = 'min(72vw, 320px)';

export interface FocusFrameProps {
  /** 0–100 ("Verificando…"): muestra la barra fina bajo el marco. Omitido, no hay barra. */
  progress?: number;
  /** Texto accesible de la barra de progreso (el mismo texto de estado visible). */
  label: string;
  /** Contenido centrado dentro del marco (el check del éxito, por ejemplo). */
  children?: ReactNode;
  /** Rellena el marco de coral: el momento "✓ Listo" (§8.1). */
  filled?: boolean;
  /**
   * T096: lado del marco en píxeles CSS, calculado desde el ROI real con `roiOverlaySide`.
   * `null` mientras no hay fotograma; entonces se usa la reserva.
   */
  sidePx?: number | null;
  /** T096: fracción del recorte vigente, para que las pruebas puedan comprobar la sincronía. */
  roiFraction?: number | null;
  'data-testid'?: string;
}

export function FocusFrame({
  progress,
  label,
  children,
  filled = false,
  sidePx = null,
  roiFraction = null,
  'data-testid': testId,
}: FocusFrameProps) {
  const determinate = typeof progress === 'number' && Number.isFinite(progress);
  const clamped = determinate ? Math.max(0, Math.min(100, progress)) : 0;
  const measured = typeof sidePx === 'number' && Number.isFinite(sidePx) && sidePx > 0;
  const side = measured ? `${sidePx}px` : FRAME_SIDE;

  return (
    <div
      data-testid={testId}
      data-filled={filled ? 'true' : 'false'}
      data-roi={measured ? 'measured' : 'pending'}
      {...(roiFraction === null ? {} : { 'data-roi-fraction': String(roiFraction) })}
      className="relative transition-[width,height] duration-fm-base ease-fm-standard motion-reduce:transition-none"
      style={{ width: side, height: side }}
    >
      {/* Velo exterior + marco. El `box-shadow` gigante oscurece todo menos el hueco. */}
      <div
        aria-hidden="true"
        className={cn(
          'absolute inset-0 rounded-fm-xl border-2 border-white/80',
          'shadow-[0_0_0_9999px_rgba(20,28,43,0.55)]',
          filled && 'bg-fm-primary transition-colors duration-fm-base ease-fm-standard',
          'motion-reduce:transition-none',
        )}
      />
      {/* Esquinas más gruesas (§8.1). */}
      <span
        aria-hidden="true"
        className="absolute top-0 left-0 h-8 w-8 rounded-tl-fm-xl border-t-4 border-l-4 border-white"
      />
      <span
        aria-hidden="true"
        className="absolute top-0 right-0 h-8 w-8 rounded-tr-fm-xl border-t-4 border-r-4 border-white"
      />
      <span
        aria-hidden="true"
        className="absolute bottom-0 left-0 h-8 w-8 rounded-bl-fm-xl border-b-4 border-l-4 border-white"
      />
      <span
        aria-hidden="true"
        className="absolute right-0 bottom-0 h-8 w-8 rounded-br-fm-xl border-r-4 border-b-4 border-white"
      />

      {/* QA-4: barra fina de progreso determinado, fuera del marco (debajo), solo en "Verificando…". */}
      {determinate ? (
        <div
          role="progressbar"
          aria-label={label}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(clamped)}
          data-testid="marking-progress"
          className="absolute top-full right-0 left-0 mt-fm-2 h-1 overflow-hidden rounded-fm-pill bg-white/30"
        >
          <div
            data-testid="marking-progress-fill"
            className="h-full rounded-fm-pill bg-fm-primary transition-[width] duration-fm-base ease-fm-standard motion-reduce:transition-none"
            style={{ width: `${clamped}%` }}
          />
        </div>
      ) : null}

      {children === undefined ? null : (
        <div className="absolute inset-0 flex items-center justify-center">{children}</div>
      )}
    </div>
  );
}
